import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const visBadge = {
  pubblico: "bg-emerald-100 text-emerald-700",
  riservato_soci: "bg-amber-100 text-amber-700",
  riservato_utente: "bg-red-100 text-red-700",
};
const visLabel = { pubblico: "Pubblico", riservato_soci: "Riservato Soci", riservato_utente: "Personale" };

function ContactForm({ onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    type: "persona_fisica", name: "", email: "", phone: "",
    codice_fiscale: "", partita_iva: "", pec: "", visibility: "pubblico",
    notes: "", tags: [],
  });
  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nome richiesto"); return; }
    try { await api.post("/contacts", form); toast.success("Contatto creato"); setOpen(false); onSaved(); }
    catch { toast.error("Errore"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Nuovo Contatto</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <RadioGroup value={form.type} onValueChange={(v)=>setForm({...form, type: v})} className="grid grid-cols-3 gap-3">
            {[["persona_fisica","Persona Fisica"],["persona_giuridica","Persona Giuridica"],["pubblica_amministrazione","P.A."]].map(([v,l]) => (
              <label key={v} className={`flex items-center gap-2 p-3 border rounded-md cursor-pointer ${form.type===v ? "border-navy-500 bg-navy-50" : "border-slate-200"}`}>
                <RadioGroupItem value={v} /> <span className="text-sm">{l}</span>
              </label>
            ))}
          </RadioGroup>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5"><Label>Nome / Ragione Sociale *</Label><Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} data-testid="contact-name-input"/></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Telefono</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e)=>setForm({...form, codice_fiscale: e.target.value})}/></div>
            <div className="space-y-1.5"><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={(e)=>setForm({...form, partita_iva: e.target.value})}/></div>
            <div className="space-y-1.5"><Label>PEC</Label><Input value={form.pec} onChange={(e)=>setForm({...form, pec: e.target.value})}/></div>
            <div className="space-y-1.5">
              <Label>Visibilità</Label>
              <Select value={form.visibility} onValueChange={(v)=>setForm({...form, visibility: v})}>
                <SelectTrigger><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pubblico">Pubblico</SelectItem>
                  <SelectItem value="riservato_soci">Riservato Soci</SelectItem>
                  <SelectItem value="riservato_utente">Solo Me</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Note interne</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})}/></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Annulla</Button>
          <Button className="bg-navy-800 hover:bg-navy-900" onClick={submit} data-testid="contact-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Contatti() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const load = async () => { const { data } = await api.get(`/contacts${q ? `?q=${q}` : ""}`); setItems(data); };
  useEffect(() => { load(); }, [q]);

  const remove = async (id) => { await api.delete(`/contacts/${id}`); load(); };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Contatti</h1>
          <p className="text-sm text-slate-500 mt-1">{items.length} contatti in rubrica</p>
        </div>
        <ContactForm onSaved={load} trigger={<Button className="bg-navy-800 hover:bg-navy-900 text-white" data-testid="new-contact-btn"><Plus className="w-4 h-4 mr-1" />Nuovo Contatto</Button>} />
      </div>

      <Card className="p-4">
        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <Input className="pl-9" placeholder="Cerca per nome, email, CF, P.IVA..." value={q} onChange={(e)=>setQ(e.target.value)} data-testid="contacts-search" />
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-5 py-2.5">Nome</th>
              <th className="text-left px-5 py-2.5">Tipo</th>
              <th className="text-left px-5 py-2.5">Email</th>
              <th className="text-left px-5 py-2.5">Telefono</th>
              <th className="text-left px-5 py-2.5">Visibilità</th>
              <th className="px-5 py-2.5"></th>
            </tr>
          </thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`contact-row-${c.id}`}>
                <td className="px-5 py-3 text-navy-900 font-medium">{c.name}</td>
                <td className="px-5 py-3 text-slate-600 capitalize">{c.type.replace("_"," ")}</td>
                <td className="px-5 py-3 text-slate-600">{c.email || "—"}</td>
                <td className="px-5 py-3 text-slate-600">{c.phone || "—"}</td>
                <td className="px-5 py-3"><Badge className={`${visBadge[c.visibility]} border-0`}>{visLabel[c.visibility]}</Badge></td>
                <td className="px-5 py-3 text-right"><button onClick={()=>remove(c.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-contact-${c.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-12">Nessun contatto.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
